import { Injectable } from '@angular/core';
import {
    FormGroup,
    FormArray,
    Validators,
    FormControl,
    AbstractControl,
    ValidatorFn,
    ValidationErrors,
} from '@angular/forms';
import {
    NestedFormGroupType,
    FormGroupType,
} from '../../../../../../core/models/form-group-type';
import { GoFrequencyPairingRequest } from '../../models/go-frequency-pairing-request';
import { RegExPattern } from '../../../../../../core/models/regexp-pattern/regex-pattern';
import { PaymentMethod } from '../../../../../../core/enums/commerce/payment/payment-method';
import { CreditCardSummaryService } from '../../../../../../core/services/commerce/credit-card-summary/credit-card-summary.service';
import { PreferredPaymentMethodFormModel } from '../../../../orders/order-details/models/preferred-payment-method-form-model';
import { PaymentSourceFormService } from '../../../../orders/shared/services/payment-source-form/payment-source-form.service';
import { CreditCardSummary } from '../../../../../../core/models/commerce/cards-on-file/credit-card-summary';
import { PaymentMethodValidatorMap } from '../../../../orders/shared/constants/payment-method-validator-map';
import { notExpiredValidator } from '../../../../orders/shared/validators/order-payment-validators';
import { GoSubmitToEwaFormModel } from '../../models/go-submit-to-ewa-form-model';
import { BehaviorSubject } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class GoSubmitToEwaFormService extends PaymentSourceFormService<PreferredPaymentMethodFormModel> {
    private isFormInvalid = new BehaviorSubject<() => boolean>(() => false);
    isFormInvalid$ = this.isFormInvalid.asObservable();

    constructor(
        private readonly creditCardSummaryService: CreditCardSummaryService,
    ) {
        super();
    }

    setFormInvalidFn(fn: () => boolean) {
        this.isFormInvalid.next(fn);
    }

    clearFormInvalidFn() {
        this.isFormInvalid.next(() => false);
    }

    getFormDefinition(): FormGroup<
        NestedFormGroupType<GoSubmitToEwaFormModel>
    > {
        return new FormGroup<NestedFormGroupType<GoSubmitToEwaFormModel>>({
            informationIsVerified: new FormControl<boolean>(false, [
                Validators.requiredTrue,
            ]),
            comments: new FormControl(null, [
                Validators.maxLength(1000),
                Validators.pattern(
                    RegExPattern.PrintableAsciiExceptAngleBracketsAndPipes,
                ),
            ]),
            frequencyPairings: new FormArray<
                FormGroup<FormGroupType<GoFrequencyPairingRequest>>
            >(
                [],
                [Validators.required, this.isAtleastOneFrequencyPairSelected()],
            ),
            paymentMethod: new FormControl<PaymentMethod | null>(
                null,
                Validators.required,
            ),
            cardOnFileId: new FormControl<number | null>(null),
        });
    }

    protected getPaymentMethodValidatorMap(
        getCreditCards: () => CreditCardSummary[],
    ): PaymentMethodValidatorMap<PreferredPaymentMethodFormModel> {
        return {
            [PaymentMethod.AuthNetAim]: {
                cardOnFileId: [
                    Validators.required,
                    notExpiredValidator(
                        getCreditCards,
                        this.creditCardSummaryService.isExpired,
                    ),
                ],
            },
            [PaymentMethod.InvoiceNet30]: {
                cardOnFileId: [],
            },
        };
    }

    private isAtleastOneFrequencyPairSelected(): ValidatorFn {
        return (
            frequencyPairingsFormArray: AbstractControl,
        ): ValidationErrors | null => {
            const frequencyPairings = frequencyPairingsFormArray as FormArray<
                FormGroup<FormGroupType<GoFrequencyPairingRequest>>
            >;

            if (!frequencyPairings) {
                return null;
            }

            let pairingsSelected = 0;
            for (const pairingControl of frequencyPairings.controls) {
                const isSelectedControl = pairingControl.controls.isSelected;

                if (isSelectedControl && isSelectedControl.value === true) {
                    pairingsSelected++;
                }
            }

            if (pairingsSelected === 0) {
                return {
                    required:
                        'At least one frequency pair result needs to be selected.',
                };
            }

            return null;
        };
    }
}
